import React, {
  type ReactNode,
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { NotificationType } from 'pages/Notification/NotificationType';
import RouteChunkFallback from 'routes/RouteChunkFallback';
import { type RootState } from 'stores';
import { ApplicationOutcome, trackApplicationOutcome } from 'utils/clarityEvents';
import { getApplicationSseSession } from 'utils/sse/applicationSseSession';
import { SseError } from 'utils/sse/SseError';

const NotificationPage = lazy(() => import('pages/Notification'));

type SseProcessingTimeoutContextValue = {
  triggerProcessingTimeout: () => void;
  setSseWatchActive: (active: boolean) => void;
  isProcessingTimeout: boolean;
};

const SseProcessingTimeoutContext = createContext<SseProcessingTimeoutContextValue | null>(null);

export const isSseProcessingError = (error: unknown): boolean => error instanceof SseError;

export const useSSEProcessingTimeout = (): SseProcessingTimeoutContextValue => {
  const context = useContext(SseProcessingTimeoutContext);

  if (!context) {
    throw new Error('useSSEProcessingTimeout must be used within SseProcessingTimeoutProvider');
  }

  return context;
};

export const useSseProcessingWatch = (active: boolean): void => {
  const { setSseWatchActive } = useSSEProcessingTimeout();

  useEffect(() => {
    setSseWatchActive(active);

    return () => {
      setSseWatchActive(false);
    };
  }, [active, setSseWatchActive]);
};

type SseProcessingTimeoutProviderProps = {
  children: ReactNode;
};

export const SseProcessingTimeoutProvider = ({ children }: SseProcessingTimeoutProviderProps) => {
  const [isProcessingTimeout, setIsProcessingTimeout] = useState(false);
  const [sseWatchActive, setSseWatchActiveState] = useState(false);
  const applicationId = useSelector<RootState, string | undefined>(
    (state) => state.application.applicationId,
  );
  const submissionId = useSelector<RootState, string | undefined>(
    (state) => state.application.submissionId,
  );

  const triggerProcessingTimeout = useCallback(() => {
    setIsProcessingTimeout((current) => {
      if (current) {
        return current;
      }

      trackApplicationOutcome(ApplicationOutcome.ProcessingTimeout);

      return true;
    });
  }, []);

  const setSseWatchActive = useCallback((active: boolean) => {
    setSseWatchActiveState(active);
  }, []);

  useEffect(() => {
    if (!sseWatchActive || isProcessingTimeout) {
      return;
    }

    const session = getApplicationSseSession();

    if (!session) {
      return;
    }

    if (submissionId && session.submissionId !== submissionId) {
      return;
    }

    const handleSseError = () => {
      if (session.eventSource.readyState !== EventSource.CLOSED) {
        return;
      }

      Sentry.captureMessage('Application SSE connection failed to reconnect during processing', {
        extra: { applicationId, submissionId: session.submissionId },
      });
      triggerProcessingTimeout();
    };

    session.eventSource.addEventListener('error', handleSseError);

    return () => {
      session.eventSource.removeEventListener('error', handleSseError);
    };
  }, [applicationId, isProcessingTimeout, sseWatchActive, submissionId, triggerProcessingTimeout]);

  const value = useMemo(
    () => ({
      triggerProcessingTimeout,
      setSseWatchActive,
      isProcessingTimeout,
    }),
    [isProcessingTimeout, setSseWatchActive, triggerProcessingTimeout],
  );

  if (isProcessingTimeout) {
    return (
      <Suspense fallback={<RouteChunkFallback />}>
        <NotificationPage type={NotificationType.ProcessingTimeout} />
      </Suspense>
    );
  }

  return (
    <SseProcessingTimeoutContext.Provider value={value}>
      {children}
    </SseProcessingTimeoutContext.Provider>
  );
};
